import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { MousePointer, Activity, Users, TrendingUp, BarChart3, PieChart, RefreshCw } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer
} from 'recharts';
import { useAdmin } from './AdminContext';

const FEATURE_DISPLAY_NAMES = {
  'training_in_progress': 'Training in Progress',
  'nutrition_tracker': 'Nutrition Tracker',
  'ai_planner': 'AI Planner',
  'walking': 'Walking Timer',
  'statistics': 'Statistics',
  'photos': 'Progress Photos',
  'timer': 'Gym Timer',
  'calendar': 'Calendar',
  'gym_finder': 'Gym Finder',
  'planner': 'Training Planner',
  'database': 'Database Search',
  'fresh_produce': 'Fresh Produce',
  'scan': 'Barcode Scan',
  'my_scanned': 'My Scanned',
  'my_meals': 'My Meals',
  'my_products': 'My Products',
  'restaurant_menu': 'Restaurant Menu',
  'meal_photo': 'Meal Photo',
  'manual_add': 'Manual Add'
};

export default function UsageTab({ refreshTrigger }) {
  const { adminFetch, API_URL } = useAdmin();
  const [usageStats, setUsageStats] = useState(null);
  const [usagePeriod, setUsagePeriod] = useState(30);

  const loadUsageStats = useCallback(async (period) => {
    try {
      const days = period ?? usagePeriod;
      const response = await adminFetch(`${API_URL}/api/v2/admin/usage-stats?days=${days}`);
      if (response.ok) {
        const data = await response.json();
        setUsageStats(data);
      }
    } catch (error) {
      console.error('Error loading usage stats:', error);
    }
  }, [adminFetch, API_URL, usagePeriod]);

  useEffect(() => { loadUsageStats(); }, [refreshTrigger]);

  return (
    <div className="space-y-6" data-testid="usage-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <MousePointer className="h-5 w-5 text-purple-400" />
          Feature Usage Analytics
        </h2>
        <div className="flex items-center gap-2">
          <select
            value={usagePeriod}
            onChange={(e) => {
              const newPeriod = Number(e.target.value);
              setUsagePeriod(newPeriod);
              loadUsageStats(newPeriod);
            }}
            className="bg-slate-700 border-slate-600 text-white rounded-lg px-3 py-2 text-sm"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <Button variant="outline" size="sm" onClick={() => loadUsageStats()} className="border-slate-600">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {usageStats ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-gradient-to-br from-purple-500/20 to-purple-600/20 border-purple-500/30">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-purple-300 text-sm">Total Feature Uses</p>
                    <p className="text-3xl font-bold text-white">{usageStats.total_uses.toLocaleString()}</p>
                  </div>
                  <Activity className="h-10 w-10 text-purple-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-blue-500/20 to-blue-600/20 border-blue-500/30">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-300 text-sm">Unique Users</p>
                    <p className="text-3xl font-bold text-white">{usageStats.total_unique_users}</p>
                  </div>
                  <Users className="h-10 w-10 text-blue-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-green-500/20 to-green-600/20 border-green-500/30">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-300 text-sm">Avg Uses/Day</p>
                    <p className="text-3xl font-bold text-white">
                      {usageStats.daily_usage.length > 0
                        ? Math.round(usageStats.total_uses / usageStats.daily_usage.length)
                        : 0}
                    </p>
                  </div>
                  <TrendingUp className="h-10 w-10 text-green-400" />
                </div>
              </CardContent>
            </Card>
          </div>

          {usageStats.daily_usage.length > 0 && (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-purple-400" />
                  Daily Usage Trend
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={usageStats.daily_usage}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis
                        dataKey="date" stroke="#9CA3AF" tick={{ fill: '#9CA3AF', fontSize: 12 }}
                        tickFormatter={(value) => { const d = new Date(value); return `${d.getMonth()+1}/${d.getDate()}`; }}
                      />
                      <YAxis stroke="#9CA3AF" tick={{ fill: '#9CA3AF', fontSize: 12 }} />
                      <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }} labelStyle={{ color: '#fff' }} />
                      <Bar dataKey="count" fill="#A855F7" name="Uses" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <PieChart className="h-5 w-5 text-purple-400" />
                Feature Usage Breakdown
              </CardTitle>
              <CardDescription className="text-slate-400">
                Most used features in the last {usagePeriod} days
              </CardDescription>
            </CardHeader>
            <CardContent>
              {usageStats.by_feature.length > 0 ? (
                <div className="space-y-3">
                  {usageStats.by_feature.map((stat, index) => {
                    const displayName = stat.sub_feature
                      ? `${FEATURE_DISPLAY_NAMES[stat.feature] || stat.feature} → ${FEATURE_DISPLAY_NAMES[stat.sub_feature] || stat.sub_feature}`
                      : FEATURE_DISPLAY_NAMES[stat.feature] || stat.feature;
                    const percentage = usageStats.total_uses > 0
                      ? ((stat.total_uses / usageStats.total_uses) * 100).toFixed(1)
                      : 0;
                    const colors = [
                      'bg-purple-500', 'bg-blue-500', 'bg-green-500', 'bg-yellow-500',
                      'bg-pink-500', 'bg-cyan-500', 'bg-orange-500', 'bg-red-500'
                    ];
                    const color = colors[index % colors.length];

                    return (
                      <div key={`${stat.feature}-${stat.sub_feature}`} className="space-y-1">
                        <div className="flex justify-between items-center">
                          <span className="text-white text-sm">{displayName}</span>
                          <div className="flex items-center gap-3">
                            <span className="text-slate-400 text-xs">{stat.unique_users} users</span>
                            <span className="text-white text-sm font-medium">{stat.total_uses.toLocaleString()} ({percentage}%)</span>
                          </div>
                        </div>
                        <div className="w-full bg-slate-700 rounded-full h-2">
                          <div className={`${color} h-2 rounded-full transition-all duration-500`} style={{ width: `${percentage}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-400">
                  <MousePointer className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No usage data available yet</p>
                  <p className="text-sm">Feature usage will appear here as users interact with the app</p>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      ) : (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-12 text-center">
            <Activity className="h-12 w-12 mx-auto text-slate-600 mb-4" />
            <p className="text-slate-400">Loading usage statistics...</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
